import { Box, CircularProgress, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useGetAccountById } from '../../../../../services/react-query/accounts';
import { useGetAllContacts } from '../../../../../services/react-query/contacts/useGetAllContacts';
import { UseGetAllEventsParams, useGetAllEvents } from '../../../../../services/react-query/events';
import { useGetAllTasks } from '../../../../../services/react-query/tasks';
import { Address } from '../../../../../types/Address';
import { Contact } from '../../../../../types/Contact';
import { Event } from '../../../../../types/Event';
import { Task, TaskStatusFilter } from '../../../../../types/Task';
import StatusPill from '../../../../../uikit/pills/StatusPill';
import { addOrRemoveDaysToDate, humanizeEventDateTimes } from '../../../../../utility/dateUtils';
import AccountTaskFilter, { TaskStatusAccountFilter } from '../../../../accounts/AccountTaskTable/AccountTaskFilter';
import TaskStatusPill from '../../../../tasks/TaskStatusPill';
import DrawerSection from '../DrawerSection';
import InfoRow from '../InfoRow';
import ModuleDrawerHeader from '../ModuleDrawerHeader';
import { moduleDrawerStyles } from '../moduleDrawerStyles';

type Props = { uuid: string };
type EventsView = 'UPCOMING' | 'PAST' | 'ALL';

const CenterSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={20} />
  </Box>
);

const AccountDrawerView: React.FC<Props> = ({ uuid }) => {
  // Account
  const { data: account, isLoading: accountLoading } = useGetAccountById({ uuid });

  // Contacts (top 5)
  const { data: contactsResp, isLoading: contactsLoading } = useGetAllContacts({
    params: { per_page: 5, filters: { account: uuid } },
  });
  const contacts: Contact[] = contactsResp?.data || [];

  // ------- Events -------
  const [eventsView, setEventsView] = useState<EventsView>('UPCOMING');
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const eventParams: UseGetAllEventsParams = useMemo(() => {
    const sort: { start_at?: 1 | -1 } = { start_at: (eventsView === 'UPCOMING' ? 1 : -1) as 1 | -1 };
    const baseFilters = { account: [uuid] } as Record<string, (string | null)[]>;

    if (eventsView === 'UPCOMING') return { sort, filters: { ...baseFilters, date_range: [today, null] } };
    if (eventsView === 'PAST')
      return { sort, filters: { ...baseFilters, date_range: [null, addOrRemoveDaysToDate({ date: today, days: -1, humanize: false })] } };
    return { sort, filters: baseFilters };
  }, [eventsView, today, uuid]);

  const { data: eventsData, isLoading: eventsLoading } = useGetAllEvents({ params: eventParams });
  const events = eventsData?.data ?? [];

  const eventsActions = (
    <Select size="small" value={eventsView} onChange={(e: SelectChangeEvent<EventsView>) => setEventsView(e.target.value as EventsView)} sx={{ minWidth: 180 }}>
      <MenuItem value="UPCOMING">Upcoming Events</MenuItem>
      <MenuItem value="PAST">Past Events</MenuItem>
      <MenuItem value="ALL">All Events</MenuItem>
    </Select>
  );

  // ------- Tasks -------
  const [taskStatus, setTaskStatus] = useState<TaskStatusAccountFilter>(TaskStatusFilter.ACTIVE);

  const rawTaskFilters: Record<string, (string | null)[] | undefined> = {
    account: [uuid],
    task_status: taskStatus !== 'all' ? [taskStatus as unknown as string] : undefined,
  };
  const taskFilters = _.pickBy(rawTaskFilters, (v): v is (string | null)[] => v !== undefined) as Record<string, (string | null)[]>;

  const { data: tasksResp, isLoading: tasksLoading } = useGetAllTasks({
    params: { per_page: 10, filters: taskFilters },
  });
  const tasks = tasksResp?.data ?? [];
  const tasksActions = <AccountTaskFilter value={taskStatus} onChange={setTaskStatus} />;

  // ------- Render -------
  if (accountLoading) return <CenterSpinner />;
  if (!account)
    return (
      <Typography variant="body2" color="error">
        Account not found
      </Typography>
    );

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(account.name)}</ModuleDrawerHeader>
      <Box sx={moduleDrawerStyles.content}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Account Information */}
          <DrawerSection title="Account Information">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <InfoRow label="Name" value={account.name} />
              <InfoRow label="Code" value={account.code} />
              <InfoRow label="Phone" value={account.phone} />
              <InfoRow label="Account Manager" value={account.manager_display_name} />
              <InfoRow label="School District" value={account.school_district_value} />
              <InfoRow label="Status" value={account.status_value} />
              <InfoRow label="Type" value={account.type_value} />
            </Box>
          </DrawerSection>

          {/* Address Information */}
          <DrawerSection title="Address Information">
            <Box sx={{ mb: 2 }}>
              {account.addresses?.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {_.map(account.addresses, (addr: Address) => {
                    const fullAddress =
                      addr.formatted_address ||
                      [addr.address_1, addr.address_2, addr.city, addr.division, addr.postal_code, addr.country].filter(Boolean).join(', ');
                    return (
                      <Box key={addr.uuid} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {addr.type_value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {fullAddress}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No addresses available
                </Typography>
              )}
            </Box>
          </DrawerSection>

          {/* Contacts */}
          <DrawerSection title="Contacts">
            {contactsLoading ? (
              <CenterSpinner />
            ) : contacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No contacts found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {_.map(contacts, (contact: Contact) => (
                  <Box key={contact.uuid}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {contact.full_name}
                    </Typography>
                    <InfoRow label="Email" value={contact.email} />
                    <InfoRow label="Phone" value={contact.phone_1} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Events */}
          <DrawerSection title="Events" actions={eventsActions}>
            {eventsLoading ? (
              <CenterSpinner />
            ) : events.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No events found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {_.map(events, (event: Event) => (
                  <Box key={event.uuid} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, borderBottom: '1px solid #eee', pb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={event.display_name}
                      >
                        {event.display_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {humanizeEventDateTimes(event)}
                      </Typography>
                    </Box>
                    <StatusPill status={event.status} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Tasks */}
          <DrawerSection title="Tasks" actions={tasksActions}>
            {tasksLoading ? (
              <CenterSpinner />
            ) : tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No tasks found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {_.map(tasks, (task: Task) => (
                  <Box key={task.uuid} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, borderBottom: '1px solid #eee', py: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={task.name}
                      >
                        {task.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[task.type_value, task.parent_type, task.parent_location].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                    </Box>
                    <TaskStatusPill status={task.status} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default AccountDrawerView;
